import { openPaymentPage } from "@/lib/open-checkout";
import { buildDigitalConsent } from "@/lib/digital-consent";
import { ReaderTrigger } from "@/components/InPageReader";
import { checkoutAttribution } from "@/lib/checkout-attribution";
import { localeOrigin, localeTo } from "@/lib/locale";
import { friendlyError } from "@/lib/friendly-errors";
import { beginCheckout, endCheckout, isCheckoutInFlight } from "@/lib/checkout-lock";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Globe2 } from "lucide-react";
import { CountrySelect } from "@/components/auth/AccountFields";
import { Sparkles, ArrowRight, HeartHandshake, Mail, KeyRound, Loader2, Eye, EyeOff, ShieldCheck, LogIn, User, Info, CreditCard } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

import { useMembership } from "@/hooks/useMembership";
import { CreateAccountCTA } from "@/components/CreateAccountCTA";
import { MembershipValue } from "@/components/signup/MembershipValue";
import { FeelsFamiliarCarousel } from "@/components/signup/FeelsFamiliarCarousel";

import { isMembershipPublic } from "@/lib/membership-launch";
import { shouldHideCommerce } from "@/lib/native-app";
import { trackFunnel, trackCheckoutFailed } from "@/lib/funnel";
import { track } from "@/lib/track";
import { formatPlanPrice, formatPlanCycle, getPlan } from "@/lib/membership-plans";
import { adoptPlanFromUrl } from "@/lib/selected-plan";
import { iapAvailable } from "@/lib/native-iap";
import { createFoundingCheckoutSession } from "@/lib/membership.functions";
import { useServerFn } from "@tanstack/react-start";
import { MembershipJourneySteps } from "@/components/membership/MembershipJourneySteps";
import { REFERRAL_SOURCE_OPTIONS } from "@/lib/referral-sources";


export const Route = createFileRoute("/signup")({
  ssr: false,
  validateSearch: (
    search: Record<string, unknown>,
  ): { next?: string; fresh?: string; plan?: string; apple?: string } => ({
    next: typeof search.next === "string" ? search.next : undefined,
    apple: typeof search.apple === "string" ? search.apple : undefined,
    plan: typeof search.plan === "string" ? search.plan : undefined,
    // ?fresh=1 arrives parsed as the number 1, so accept both shapes —
    // otherwise the flag is stripped and the escape hatch never fires.
    fresh:
      typeof search.fresh === "string"
        ? search.fresh
        : typeof search.fresh === "number"
          ? String(search.fresh)
          : undefined,
  }),


  head: () => ({
    meta: [
      { title: "Start Your Membership — Recovery Trauma™" },
      {
        name: "description",
        content:
          "See what the Recovery Trauma™ Membership includes, then create your secure account. $14.99 USD/month, cancel anytime.",
      },
      { property: "og:title", content: "Start Your Membership — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "See what the Recovery Trauma™ Membership includes, then create your secure account. $14.99 USD/month, cancel anytime.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/signup" }],
  }),

  component: SignupRoute,
});

function SignupRoute() {
  const { loading, isAdmin } = useMembership();
  // Pre-launch account creation is open to everyone (Jana, 13 July 2026 brief).
  // We still show a soft admin marker so testing sessions are obvious.
  if (loading) {
    return (
      <div className="min-h-dvh bg-background px-4 py-16 sm:py-24">
        <div className="mx-auto flex max-w-sm items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      </div>
    );
  }
  return (
    <>
      {isAdmin ? (
        <div className="mx-auto mt-4 flex max-w-sm items-center justify-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--gold-deep)]">
          <ShieldCheck className="h-3.5 w-3.5" /> Admin Test Mode — signed in as admin
        </div>
      ) : null}
      <MemberSignupPage />
    </>
  );
}


const REFERRAL_SOURCES = REFERRAL_SOURCE_OPTIONS;

function MemberSignupPage() {
  const { next, fresh: freshParam, plan: planParam, apple: applePlan } = Route.useSearch();
  // iOS app: new joiners see the Apple plans first, then create their account.
  useEffect(() => {
    if (iapAvailable() && !applePlan) {
      window.location.replace(localeTo("/app-membership"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applePlan]);
  const fresh = freshParam === "1";

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [country, setCountry] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [referralSource, setReferralSource] = useState("");
  const [referralDetail, setReferralDetail] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [existingAccount, setExistingAccount] = useState(false);
  const [needsConfirm, setNeedsConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [giftCode, setGiftCode] = useState("");
  /** Gift-code holders skip payment and go straight to redeeming. */
  async function continueAfterAccount() {
    const code = giftCode.trim();
    if (code) {
      window.location.assign(`/redeem-gift?code=${encodeURIComponent(code)}`);
      return;
    }
    await goToCheckout();
  }
  // Which payment method the visitor pressed — card or PayPal.
  const paypalPreferred = useRef(false);
  const [authChecking, setAuthChecking] = useState(true);

  const [selectedPlanId, setSelectedPlanIdState] = useState<string | null>(null);
  const plan = getPlan(selectedPlanId);
  const startCheckout = useServerFn(createFoundingCheckoutSession);
  // Inside the iOS app, prices and external checkout are hidden: account
  // creation is free here and members continue to the in-app Plans screen.
  const hideCommerce = shouldHideCommerce();

  useEffect(() => {
    // ?plan= wins over storage, then gets re-persisted for the payment step.
    setSelectedPlanIdState(adoptPlanFromUrl(planParam));
  }, [planParam]);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const firstNameRef = useRef<HTMLInputElement | null>(null);
  /** True from the moment the join form is submitted until we leave the page. */
  const submitting = useRef(false);

  useEffect(() => {
    // Autofocus first name once the checking spinner clears.
    if (!authChecking) firstNameRef.current?.focus();
  }, [authChecking]);

  useEffect(() => {
    let cancelled = false;
    try { sessionStorage.removeItem("rt_pending_verify_email"); } catch { /* noop */ }
    // An existing account must NEVER be shown Step 1 again. If a session is
    // present we resolve their real position in the journey (verify email →
    // choose payment → active) and send them straight there. Only genuinely
    // signed-out visitors, or someone who explicitly asked for a fresh start
    // (?fresh=1), see the create-account form.
    (async () => {
      if (fresh) {
        try { await supabase.auth.signOut(); } catch { /* noop */ }
        if (!cancelled) setAuthChecking(false);
        return;
      }
      try {
        const { resolveOnboardingDestination } = await import(
          "@/lib/onboarding-destination"
        );
        const target = await resolveOnboardingDestination();
        if (cancelled) return;
        // Someone who is mid-join must never be navigated away: signing in
        // re-runs this check, and a full page change here would throw away
        // the payment request that is already on its way, landing a brand
        // new member on "complete your membership" instead of the card page.
        if (target && !submitting.current && !isCheckoutInFlight()) {
          window.location.assign(localeTo(target));
          return;
        }
        if (submitting.current || isCheckoutInFlight()) return;
      } catch { /* fall through to the form */ }
      if (!cancelled) setAuthChecking(false);
    })();
    const timeout = window.setTimeout(() => {
      if (!cancelled) setAuthChecking(false);
    }, 4_000);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [fresh]);


  useEffect(() => {
    trackFunnel(
      "page_view",
      { source: "signup", dedupeKey: "signup_view" },
      { surface: "signup_step_1" },
    );
  }, []);

  if (authChecking) {
    return (
      <div className="min-h-dvh bg-background px-4 py-16 sm:py-24">
        <div className="mx-auto flex max-w-sm items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking your account…
        </div>
      </div>
    );
  }




  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(""); setInfo(""); setExistingAccount(false);
    if (!firstName.trim()) { setError("Please enter your first name."); return; }
    if (!lastName.trim()) { setError("Please enter your surname."); return; }
    if (!email.trim()) { setError("Please enter your email address."); return; }
    if (!country) { setError("Please choose your country."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirmPassword) { setError("Both passwords need to match."); return; }

    if (!agreeTerms) { setError("Please confirm you're 18+ and agree to the Terms & Privacy Policy."); return; }
    // The account already exists and is waiting for email confirmation:
    // never create it a second time (that trips the email cooldown and shows
    // a confusing "request this after N seconds" error). Just try to continue.
    if (needsConfirm) {
      setLoading(true);
      const { error: again } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      setLoading(false);
      if (again) {
        setInfo(
          `Your account is already created. Please open the email we sent to ${email.trim()}, tap the confirmation link, then press "I've confirmed my email — continue to payment". Check your spam folder if you can't see it.`,
        );
        return;
      }
      await continueAfterAccount();
      return;
    }
    // Stash the pending address BEFORE calling signUp so /verify-email can
    // render its "check your email" state even if onAuthStateChange (which
    // may redirect us) fires before this handler resumes.
    try { sessionStorage.setItem("rt_pending_verify_email", email.trim()); } catch { /* noop */ }
    setLoading(true);
    trackFunnel("signup_started", { source: "signup", plan: plan.id, dedupeKey: "signup_started" });
    trackFunnel("email_entered", { source: "signup", dedupeKey: "signup_email" });
    trackFunnel("checkout_clicked", { source: "signup" }, { step: "create_account_submitted" });
    const displayName = [firstName.trim(), lastName.trim()].filter(Boolean).join(" ");
    const { data, error: signUpError } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        emailRedirectTo:
          localeOrigin() +
          "/auth/callback?next=" +
          encodeURIComponent(
            giftCode.trim()
              ? `/redeem-gift?code=${encodeURIComponent(giftCode.trim())}`
              : "/membership",
          ),
        data: {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          display_name: displayName,
          country,

          ...(referralSource
            ? {
                referral_source: referralSource,
                ...(referralSource === "Other" && referralDetail.trim()
                  ? { referral_source_detail: referralDetail.trim().slice(0, 120) }
                  : {}),
              }
            : {}),
          age_confirmed_18: true,
          terms_accepted_at: new Date().toISOString(),
          terms_version: "2026-07-13",
        },
      },
    });
    setLoading(false);
    if (signUpError) {
      setError(
        /security purposes|rate limit|after \d+ seconds/i.test(signUpError.message)
          ? "We've just sent you a confirmation email. Please check your inbox (and spam folder), tap the link, then continue. If nothing arrives, wait a minute and try again."
          : signUpError.message,
      );
      track("signup_error", { message: signUpError.message }, { source: "signup" });
      return;
    }
    // Supabase returns an obfuscated user with an empty `identities` array
    // when the address already exists. Never create a second journey for
    // them — point them at sign-in so they resume at the right step.
    if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      try { sessionStorage.removeItem("rt_pending_verify_email"); } catch { /* noop */ }
      setInfo(
        "An account with this email already exists. Log in below, or reset your password if you can't remember it.",
      );
      setExistingAccount(true);
      return;
    }
    if (data.user) {
      trackFunnel("account_created", {
        source: "signup",
        plan: plan.id,
        price_usd: plan.priceCents / 100,
        dedupeKey: `signup_acct:${data.user.id}`,
      });
      try {
        sessionStorage.setItem("rt_pending_verify_email", email.trim());
      } catch { /* ignore storage errors */ }
      // Never show a sign-in screen to someone who just created an account.
      // If signUp didn't return a session (email confirmation enabled), sign
      // them in with the credentials they just chose so checkout continues.
      if (!data.session) {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });
        if (signInError) {
          setNeedsConfirm(true);
          trackCheckoutFailed({
            source: "signup",
            provider: "stripe",
            plan: plan.id,
            price_usd: plan.priceCents / 100,
            price_id: plan.stripePriceId,
            reason: `email_confirmation_required: ${signInError.message}`,
            stage: "redirect",
            fallback: "confirm_email_prompt",
          });
          setInfo(
            `Your account is ready. Please check ${email.trim()} to confirm it, then continue to payment.`,
          );
          return;
        }
      }
      await continueAfterAccount();
      return;
    }

    setInfo(`Check ${email.trim()} to confirm your account, then sign in.`);



  };




  /**
   * Send a just-created account straight into Stripe Checkout for the plan
   * they picked before signing up. Any failure is recorded with its real
   * reason and hands over to /complete-membership, which explains what is
   * still outstanding — never a silent redirect.
   */
  async function goToCheckout() {
    // In the iOS app there is no external checkout: the account is created
    // and the member chooses a plan on the in-app Plans screen.
    if (hideCommerce) {
      window.location.assign(
        localeTo(applePlan ? `/app-membership?plan=${encodeURIComponent(applePlan)}` : "/app-membership"),
      );
      return;
    }
    // Someone who chose PayPal goes to the payment step where the PayPal
    // button lives, rather than straight into Stripe.
    if (paypalPreferred.current) {
      window.location.assign(
        localeTo(`/complete-membership?plan=${encodeURIComponent(plan.id)}&pay=paypal`),
      );
      return;
    }
    setLoading(true);
    const origin = localeOrigin();
    let failure: string | null = null;
    trackFunnel(
      "redirect_started",
      { source: "signup", provider: "stripe", plan: plan.id, price_usd: plan.priceCents / 100 },
      { price_id: plan.stripePriceId },
    );
    // Signing in immediately before this call fires an auth event that makes
    // the router invalidate, which cancels the in-flight checkout request.
    // A cancelled/offline attempt is retried rather than treated as a real
    // failure, so a brand-new member still lands on the payment page.
    const isTransient = (raw: string) => {
      const m = raw.toLowerCase();
      return (
        m.includes("abort") ||
        m.includes("failed to fetch") ||
        m.includes("network") ||
        m.includes("couldn't reach the server") ||
        m.includes("load failed")
      );
    };
    beginCheckout();
    try {
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const res = (await startCheckout({
            data: {
              plan_id: plan.id,
              digital_consent: buildDigitalConsent(),
              attribution: checkoutAttribution(),
              success_url: `${origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
              cancel_url: `${origin}/complete-membership`,
            } as never,
          })) as { ok?: boolean; url?: string; message?: string; status?: string } | null;
          if (res?.ok && res.url) {
            trackFunnel(
              "checkout_session_created",
              {
                source: "signup",
                provider: "stripe",
                plan: plan.id,
                price_usd: plan.priceCents / 100,
              },
              { price_id: plan.stripePriceId, attempt: attempt + 1 },
            );
            openPaymentPage(res.url);
            return;
          }
          failure = res?.message ?? res?.status ?? "checkout_session_not_created";
          break;
        } catch (e) {
          const raw =
            e instanceof Error ? e.message : typeof e === "string" ? e : String(e ?? "");
          failure = friendlyError(e);
          if (!isTransient(raw) && !isTransient(failure)) break;
          if (attempt < 2) await new Promise((r) => setTimeout(r, 700 * (attempt + 1)));
        }
      }
    } finally {
      endCheckout();
      setLoading(false);
    }
    trackCheckoutFailed({
      source: "signup",
      provider: "stripe",
      plan: plan.id,
      price_usd: plan.priceCents / 100,
      price_id: plan.stripePriceId,
      reason: failure ?? "unknown",
      stage: "session_create",
      fallback: "/complete-membership",
    });
    console.error("[signup] checkout creation failed:", failure);
    window.location.assign(
      localeTo(
        `/complete-membership?plan=${encodeURIComponent(plan.id)}&reason=${encodeURIComponent(
          (failure ?? "unknown").slice(0, 200),
        )}`,
      ),
    );
  }

  /** Recovery path for the rare case where the new account needs email confirmation. */
  async function retrySignInAndCheckout() {
    setError("");
    setLoading(true);
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    setLoading(false);
    if (signInError) {
      setError(
        "That confirmation hasn't come through yet. Open the link in your email, then try again — there's no rush.",
      );
      return;
    }
    setNeedsConfirm(false);
    await goToCheckout();
  }

  const inputCls =
    "block h-12 min-w-0 max-w-full w-full rounded-lg border border-input bg-background px-3 text-base outline-none focus:border-gold";
  const labelSpanCls =
    "mb-1 flex items-center gap-1.5 text-[15px] font-medium text-foreground/80 sm:text-base";

  return (
    <div className="signup-page relative flex min-h-[80vh] w-full min-w-0 max-w-full items-start justify-center overflow-x-hidden px-4 py-6 min-[430px]:px-5 sm:py-6">
      {/* Ambient background glow */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-32 start-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-gold/20 blur-3xl" />
        <div className="absolute bottom-0 end-0 h-64 w-64 rounded-full bg-cream/60 blur-3xl" />
      </div>

      <div className="w-full min-w-0 max-w-md">
        <div className="mb-2 text-center">
          <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--gradient-gold)] text-foreground shadow-[var(--shadow-gold)] sm:h-9 sm:w-9 sm:rounded-xl">
            <HeartHandshake className="h-5 w-5 sm:h-4 sm:w-4" />
          </div>
          {!hideCommerce && (
          <p className="mx-auto mt-3 inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-cream/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--gold-deep)] backdrop-blur sm:mt-2 sm:px-2 sm:py-0.5 sm:text-[9.5px]">
            <Sparkles className="h-3 w-3" /> Step 2 of 4
          </p>
          )}
          <h1 className="mt-3 font-serif text-3xl leading-tight text-foreground sm:mt-2 sm:text-3xl">
            Create your account
          </h1>
        </div>


        {/* Your selected membership — chosen before this step, never lost. */}
        {!hideCommerce && (
        <div className="mx-auto mt-3 flex max-w-md flex-wrap items-center justify-between gap-2 rounded-2xl border border-gold/40 bg-cream/60 px-4 py-3 text-start dark:bg-cream/10">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
              Your selected membership
            </p>
            <p className="mt-1 font-display text-sm text-foreground">{plan.shortName}</p>
            <p className="text-xs text-muted-foreground">
              {formatPlanPrice(plan)} {formatPlanCycle(plan)} · Renews automatically until cancelled
            </p>
          </div>
          <Link
            to="/choose-membership"
            className="text-xs font-medium text-[var(--gold-deep)] underline-offset-4 hover:underline"
          >
            Change plan
          </Link>
        </div>
        )}

        {/* Account creation comes first — the form is visible immediately. */}
        <div id="create-account" className="mt-4 text-center sm:mt-3">
          {!hideCommerce && (
          <p className="mx-auto flex max-w-md items-start gap-2 rounded-xl border border-gold/35 bg-cream/60 px-3 py-2 text-start text-xs leading-relaxed text-foreground dark:bg-cream/10 sm:py-1.5 sm:text-[11px]">
            <Info className="mt-0.5 h-3.5 w-3.5 flex-none text-[var(--gold-deep)]" />
            <span>No payment is taken on this step.</span>
          </p>
          )}

          {!hideCommerce && (
            <MembershipJourneySteps current={2} className="mx-auto mb-4 mt-3 max-w-md sm:mb-3 sm:mt-2" />
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          className="min-w-0 max-w-full space-y-3 overflow-hidden rounded-2xl border border-gold/25 bg-card/80 p-4 shadow-[0_20px_60px_-30px_rgba(140,105,40,0.35)] backdrop-blur-sm sm:space-y-3 sm:p-4"
        >
          {/* Three fields only — everything else is collected later in the profile. */}
          <section className="min-w-0 max-w-full space-y-2.5">

            <label className="block min-w-0 max-w-full">
              <span className={labelSpanCls}><User className="h-3.5 w-3.5" /> First name</span>
              <input
                ref={firstNameRef}
                type="text"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
                maxLength={80}
                placeholder="Jane"
                className={inputCls}
              />
            </label>
            <label className="block min-w-0 max-w-full">
              <span className={labelSpanCls}><User className="h-3.5 w-3.5" /> Surname</span>
              <input
                type="text"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
                maxLength={80}
                placeholder="Smith"
                className={inputCls}
              />
            </label>

            <label className="block min-w-0 max-w-full">
              <span className={labelSpanCls}><Mail className="h-3.5 w-3.5" /> Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className={inputCls}
              />
            </label>
            <label className="block min-w-0 max-w-full">
              <span className={labelSpanCls}><Globe2 className="h-3.5 w-3.5" /> Country</span>
              <CountrySelect id="signup-country" value={country} onChange={setCountry} className={inputCls} />
            </label>
            <label className="block min-w-0 max-w-full">
              <span className={labelSpanCls}><KeyRound className="h-3.5 w-3.5" /> Password</span>
              <div className="relative min-w-0 max-w-full">
                <input
                  id="signup-password"
                  type={showPassword ? "text" : "password"}
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  className={inputCls + " pe-12"}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  aria-controls="signup-password"
                  title={showPassword ? "Hide password" : "Show password"}
                  className="absolute end-0.5 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {showPassword ? <EyeOff aria-hidden="true" className="h-4 w-4" /> : <Eye aria-hidden="true" className="h-4 w-4" />}
                </button>
              </div>
              <span className="mt-1 block text-[11px] text-muted-foreground">At least 8 characters. Tap the eye to show or hide it.</span>
            </label>
            <label className="block min-w-0 max-w-full">
              <span className={labelSpanCls}><KeyRound className="h-3.5 w-3.5" /> Confirm password</span>
              <div className="relative min-w-0 max-w-full">
                <input
                  id="signup-confirm-password"
                  type={showConfirm ? "text" : "password"}
                  required
                  minLength={8}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  className={inputCls + " pe-12"}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((s) => !s)}
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                  aria-pressed={showConfirm}
                  aria-controls="signup-confirm-password"
                  title={showConfirm ? "Hide password" : "Show password"}
                  className="absolute end-0.5 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {showConfirm ? <EyeOff aria-hidden="true" className="h-4 w-4" /> : <Eye aria-hidden="true" className="h-4 w-4" />}
                </button>
              </div>
              {confirmPassword.length > 0 && confirmPassword !== password ? (
                <span className="mt-1 block text-[11px] text-destructive">The two passwords don't match yet.</span>
              ) : (
                <span className="mt-1 block text-[11px] text-muted-foreground">Enter your password again to be sure.</span>
              )}
            </label>

            <label className="block min-w-0 max-w-full">
              <span className={`${labelSpanCls} flex-wrap`}>
                <HeartHandshake className="h-3.5 w-3.5" /> How did you find us?{" "}
                <span className="font-normal normal-case text-muted-foreground">(optional)</span>
              </span>
              <select
                id="signup-referral-source"
                value={referralSource}
                onChange={(e) => setReferralSource(e.target.value)}
                className={inputCls}
              >
                <option value="">Prefer not to say</option>
                {REFERRAL_SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              {referralSource === "Other" ? (
                <input
                  type="text"
                  value={referralDetail}
                  onChange={(e) => setReferralDetail(e.target.value.slice(0, 120))}
                  maxLength={120}
                  placeholder="Where did you hear about us?"
                  aria-label="Where did you hear about us?"
                  className={inputCls + " mt-2"}
                />
              ) : null}
              <span className="mt-1 block max-w-full break-words text-[11px] leading-relaxed text-muted-foreground">
                This just helps us know where people are finding Recovery Trauma™.
              </span>
            </label>

            <label className="block min-w-0 max-w-full">
              <span className={`${labelSpanCls} flex-wrap`}>
                🎁 Have a gift code?{" "}
                <span className="font-normal normal-case text-muted-foreground">(optional)</span>
              </span>
              <input
                id="signup-gift-code"
                type="text"
                value={giftCode}
                onChange={(e) => setGiftCode(e.target.value.toUpperCase().slice(0, 32))}
                placeholder="e.g. RT-XXXX-XXXX"
                autoCapitalize="characters"
                autoComplete="off"
                className={inputCls}
              />
              <span className="mt-1 block max-w-full break-words text-[11px] leading-relaxed text-muted-foreground">
                With a gift code you won't need to pay — your gift membership starts as soon as your account is ready.
              </span>
            </label>


          </section>

          {/* Single combined consent. The two policy triggers sit outside the
              <label>: a control inside a label also ticks that label's box,
              and nobody should agree to something just by trying to read it. */}
          <div className="flex min-w-0 max-w-full items-start gap-2 text-xs leading-relaxed text-foreground/80">
            <input
              type="checkbox"
              id="signup-terms"
              required
              checked={agreeTerms}
              onChange={(e) => setAgreeTerms(e.target.checked)}
              className="mt-0.5 h-4 w-4 flex-shrink-0 accent-[var(--gold-deep)]"
            />
            <span className="min-w-0 flex-1 break-words">
              <label htmlFor="signup-terms">
                I'm <strong>18 or older</strong> and agree to the
              </label>{" "}
              <ReaderTrigger
                to="/terms"
                className="font-medium text-[var(--gold-deep)]"
              >
                Terms &amp; Conditions
              </ReaderTrigger>{" "}
              and{" "}
              <ReaderTrigger
                to="/privacy"
                className="font-medium text-[var(--gold-deep)]"
              >
                Privacy Policy
              </ReaderTrigger>
              .
            </span>
          </div>


          {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
          {info && <p className="rounded-md bg-gold/10 px-3 py-2 text-sm text-[var(--gold-deep)]">{info}</p>}
          {existingAccount && (
            <div className="grid grid-cols-2 gap-2">
              <Link
                to="/login"
                className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-full bg-[var(--gold-deep)] px-3 text-sm font-semibold text-[var(--cream,#FBF7EF)]"
              >
                <LogIn className="h-4 w-4" aria-hidden /> Log in
              </Link>
              <Link
                to="/forgot-password"
                className="inline-flex min-h-[44px] items-center justify-center rounded-full border border-gold/40 px-3 text-sm font-semibold text-foreground"
              >
                Reset password
              </Link>
            </div>
          )}
          {needsConfirm && (
            <>
              <button
                type="button"
                onClick={retrySignInAndCheckout}
                disabled={loading}
                className="inline-flex min-h-[48px] w-full items-center justify-center rounded-lg border border-gold/50 px-4 text-sm font-medium text-[var(--gold-deep)] transition hover:bg-gold/10 disabled:opacity-60"
              >
                I've confirmed my email — continue to payment
              </button>
              <Link
                to="/account-created"
                search={{ email: email.trim() || undefined }}
                className="text-center text-xs font-medium text-[var(--gold-deep)] hover:underline"
              >
                See what happens next
              </Link>
            </>
          )}

          {hideCommerce ? (
            <Button
              type="submit"
              disabled={loading || !agreeTerms}
              className="grid min-h-[52px] min-w-0 w-full max-w-full grid-cols-[auto_minmax(0,1fr)_auto] gap-2 overflow-hidden whitespace-normal rounded-xl px-3 shadow-sm disabled:opacity-55"
            >
              <span className="pay-icon-ring flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <HeartHandshake className="h-5 w-5" />}
              </span>
              <span className="min-w-0 flex-1 text-start">
                <span className="pay-label block font-display text-[15px] font-semibold leading-tight">
                  Create my account
                </span>
                <span className="pay-sub mt-0.5 block text-xs font-normal">
                  Free to create — choose a plan afterwards
                </span>
              </span>
              <ArrowRight className="h-4 w-4 shrink-0" />
            </Button>
          ) : (
          <fieldset className="space-y-3 border-0 p-0">
            <legend className="mb-2 w-full text-center font-display text-sm text-foreground">
              Choose how you’d like to pay
            </legend>

          <Button
            type="submit"
            disabled={loading || !agreeTerms}
            onClick={() => { paypalPreferred.current = false; }}
            className="min-h-[64px] w-full overflow-hidden whitespace-normal rounded-xl px-4 shadow-sm disabled:opacity-55"
          >
            <span className="pay-icon-ring flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <CreditCard className="h-5 w-5" />}
            </span>
            <span className="min-w-0 flex-1 text-start">
              <span className="pay-label block font-display text-[15px] font-semibold leading-tight">Pay by card</span>
              <span className="pay-sub mt-0.5 block text-xs font-normal">Create your account, then open secure card payment</span>
            </span>
            <ArrowRight className="h-4 w-4 shrink-0" />
          </Button>

          {/* PayPal is always offered alongside card — never hidden behind a fallback. */}
          <Button
            type="submit"
            variant="goldOutline"
            disabled={loading || !agreeTerms}
            onClick={() => { paypalPreferred.current = true; }}
            className="grid min-h-[64px] w-full grid-cols-[auto_minmax(0,1fr)_auto] gap-3 overflow-hidden whitespace-normal rounded-xl bg-card px-4 text-start shadow-sm disabled:opacity-55"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold/15">
              <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
                <path fill="currentColor" d="M7.5 20.5H5.2c-.3 0-.5-.2-.4-.5L6.9 4.6c.05-.3.3-.5.6-.5h5.4c2.7 0 4.4 1.4 4 4-.5 3-2.9 4.6-6 4.6H9.2c-.3 0-.5.2-.6.5l-.7 6.7c-.05.3-.2.6-.4.6z"/>
              </svg>
            </span>
            <span className="min-w-0">
              <span className="block font-display text-[15px] font-semibold leading-tight">Pay with PayPal</span>
              <span className="mt-0.5 block text-xs font-normal text-muted-foreground">Create your account, then continue securely to PayPal</span>
            </span>
            <ArrowRight className="h-4 w-4 shrink-0" />
          </Button>
          </fieldset>
          )}

          {!hideCommerce && (
          <p className="text-center text-xs text-muted-foreground">
            Pay securely by card or PayPal — whichever you prefer.
          </p>
          )}
        </form>

        <div className="mt-4 text-center text-xs text-muted-foreground">
          Already have an account? <Link to="/login" className="font-medium text-[var(--gold-deep)] hover:underline">Sign in</Link>
        </div>

        {/* Recognition and value below the form, for anyone still deciding. */}
        <FeelsFamiliarCarousel />
        <MembershipValue />


        <p className="mt-8 text-center text-xs text-muted-foreground">Healing Beyond Survival Mode™</p>
      </div>
    </div>
  );
}

function InvitationOnlyPage() {
  return (
    <div className="min-h-dvh bg-background px-4 py-16 sm:py-24">
      <div className="mx-auto w-full max-w-xl rounded-3xl border border-gold/30 bg-card p-8 text-center shadow-sm sm:p-12">
        <span className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
          <Sparkles className="h-3.5 w-3.5" /> Members only

        </span>
        <h1 className="mt-5 font-display text-3xl text-foreground sm:text-4xl">
          Membership is open
        </h1>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Recovery Trauma™ is open to members. Create your account to start, or sign in if you already
          have one.
        </p>
        <div className="mt-8 flex flex-col gap-3">
          <CreateAccountCTA size="lg" />
          <Link
            to="/login"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-gold/40 bg-cream/60 px-6 py-3 font-display text-sm font-medium text-[var(--gold-deep)] backdrop-blur transition hover:-translate-y-0.5 hover:bg-cream"
          >
            <LogIn className="h-4 w-4" /> Already invited? Log In
          </Link>
        </div>
        <p className="mt-6 text-xs text-muted-foreground">Healing Beyond Survival Mode™</p>
      </div>
    </div>
  );
}
